/**
 * Gift cards ("darilni boni") on the storefront: the amounts a market sells,
 * the text limits, and the code format.
 *
 * The amounts and the code rules are a copy of photo-upload-backend's
 * src/core/gift-card/gift-card-amounts.ts and gift-card-code.ts. The backend
 * is the authority — it refuses an amount outside its table with 400
 * GIFT_CARD_AMOUNT and caps the texts itself — so a drift here fails a
 * purchase rather than making a wrong one. gift-card.test.ts holds the
 * backend's table verbatim to catch that drift before a customer does.
 */

/** Same markets as the backend's `Market`, keyed by storefront TLD. */
export type GiftCardMarket = "si" | "hr" | "sk" | "at" | "de" | "pl" | "cz" | "ro";

/** Minor units, cheapest first. Four per market; the third is the default. */
export const GIFT_CARD_AMOUNTS: Record<GiftCardMarket, number[]> = {
  si: [2000, 3000, 5000, 10000],
  hr: [2000, 3000, 5000, 10000],
  sk: [2000, 3000, 5000, 10000],
  at: [2000, 3000, 5000, 10000],
  de: [2000, 3000, 5000, 10000],
  pl: [10000, 15000, 25000, 50000],
  cz: [50000, 75000, 125000, 250000],
  ro: [10000, 15000, 25000, 50000],
};

export const GIFT_CARD_TEXT_LIMITS = { toName: 40, fromName: 40, message: 200 } as const;

/** What the card is printed in, per market — same map as DOMAIN_MAP_TO_CURRENCY. */
const CURRENCY_SYMBOL: Record<GiftCardMarket, string> = {
  si: "€",
  hr: "€",
  sk: "€",
  at: "€",
  de: "€",
  pl: "zł",
  cz: "Kč",
  ro: "Lei",
};

export function giftCardAmountsFor(market: GiftCardMarket | undefined): number[] {
  return market ? (GIFT_CARD_AMOUNTS[market] ?? []) : [];
}

/** The pre-selected chip: 50 EUR, or the market's equivalent. */
export function defaultGiftCardAmount(market: GiftCardMarket | undefined): number | undefined {
  const amounts = giftCardAmountsFor(market);
  return amounts[2] ?? amounts[0];
}

/**
 * The market a storefront host sells in, read off its last label so stage
 * hosts resolve like production (nakup.stage.fotodelavnica.si → si).
 *
 * Undefined for localhost and anything unrecognised, as on the backend:
 * guessing "si" would offer Slovenian amounts on a host the backend will
 * then refuse to sell on.
 */
export function giftCardMarketFromHostname(hostname: string): GiftCardMarket | undefined {
  const parts = (hostname || "").split(":")[0].toLowerCase().replace(/\.$/, "").split(".");
  if (parts.length < 2) return undefined;
  const tld = parts[parts.length - 1];
  return tld in GIFT_CARD_AMOUNTS ? (tld as GiftCardMarket) : undefined;
}

/**
 * "50 €", "250 zł", "1.250 Kč". Every amount on sale is whole, so no decimals;
 * the thousands dot matches formatCurrency's de-EU grouping elsewhere in the shop.
 */
export function formatGiftCardAmount(cents: number, market: GiftCardMarket): string {
  const whole = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(
    Math.round(cents / 100),
  );
  return `${whole} ${CURRENCY_SYMBOL[market]}`;
}

/** ISO currency per market — what the backend's COUNTRY_TO_CURRENCY stores on the card. */
const CURRENCY_CODE: Record<GiftCardMarket, string> = {
  si: "EUR",
  hr: "EUR",
  sk: "EUR",
  at: "EUR",
  de: "EUR",
  pl: "PLN",
  cz: "CZK",
  ro: "RON",
};

/** The eight shop locales → BCP 47, as the backend's INTL_TAG; anything else is en-GB. */
const FACE_VALUE_INTL_TAG: Record<string, string> = {
  sl: "sl-SI",
  hr: "hr-HR",
  sk: "sk-SK",
  cs: "cs-CZ",
  pl: "pl-PL",
  "de-AT": "de-AT",
  en: "en-GB",
  it: "it-IT",
};

/** What the shop calls a currency when ICU only has its ISO code for it. */
const SHOP_SYMBOL: Record<string, string> = { EUR: "€", CZK: "Kč", PLN: "zł", RON: "Lei" };

/**
 * The amount exactly as the printed card shows it: a port of the backend's
 * `formatCreditMoney` (src/core/credit/credit-grant-email.ts), which
 * gift-card-pdf.ts prints the face value with, in the order's language —
 * "50,00 €" in Slovene, "1 250,00 Kč" in Czech, "€ 50,00" in Austria.
 *
 * Always two decimals, ICU's thin gaps folded onto a no-break space, and the
 * shop's symbol where ICU would print a bare ISO code ("RON 250.00" in
 * English → "Lei 250.00"). For the preview only; the chips and the button
 * keep the short "50 €" form.
 */
export function formatGiftCardFaceValue(
  cents: number,
  market: GiftCardMarket,
  locale: string,
): string {
  const code = CURRENCY_CODE[market];
  const tag = FACE_VALUE_INTL_TAG[locale] ?? "en-GB";
  const amount = Number((cents / 100).toFixed(2));
  const formatted = new Intl.NumberFormat(tag, {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(amount)
    .replace(/[\u202f\u2009]/g, "\u00a0");
  return formatted.includes(code) ? formatted.replace(code, SHOP_SYMBOL[code]) : formatted;
}

/** 30 characters, none that look like another (no 0 O 1 I L U). */
export const GIFT_CARD_ALPHABET = "ABCDEFGHJKMNPQRSTVWXYZ23456789";
const CODE_LENGTH = 16;

/** What someone typed → 16 upper-case alphabet characters, or null. */
export function normalizeGiftCardCode(input: string): string | null {
  const raw = String(input ?? "").toUpperCase().replace(/[\s-]/g, "");
  if (raw.length !== CODE_LENGTH) return null;
  for (const c of raw) if (!GIFT_CARD_ALPHABET.includes(c)) return null;
  return raw;
}

/**
 * For a code field, on every keystroke: upper-cases, drops spaces and dashes
 * the person typed or pasted, and puts the dashes back every four characters
 * (`abcdefgh` → `ABCD-EFGH`). Characters outside the alphabet are kept, so a
 * mistyped O stays visible for normalizeGiftCardCode to refuse rather than
 * silently vanishing from under the cursor.
 */
export function formatGiftCardCodeInput(value: string): string {
  const raw = String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, CODE_LENGTH);
  return (raw.match(/.{1,4}/g) ?? []).join("-");
}
